import { SupportedCurrency } from '../types';

const getCurrencyUnitConvertor = (
  currency: SupportedCurrency = 'INR',
  locale: string
): number => {
  const tempFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });
  const { maximumFractionDigits } = tempFormatter.resolvedOptions();
  const fractionDigits = maximumFractionDigits ?? 0;

  return fractionDigits;
};

export const formatCurrency = (
  amt: number,
  currency: SupportedCurrency = 'INR',
  locale: string
) => {
  const fractionDigits = getCurrencyUnitConvertor(currency, locale);

  // Convert from minor units to major units
  const majorAmount = amt / Math.pow(10, fractionDigits);

  // Use single formatter (integer vs decimal can be handled by options)
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: majorAmount % 1 === 0 ? 0 : fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  // Format and remove redundant symbols if needed (same as your previous replace)
  return formatter.format(majorAmount).replace(/^(\D+)/, '$1');
};

export function getCurrencySymbol(currency: SupportedCurrency, locale: string) {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const parts = formatter.formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value || currency;
}

export function formatCompactCurrency(
  amountInMinor: number,
  currency: SupportedCurrency,
  locale: string
): string {
  const fractionDigits = getCurrencyUnitConvertor(currency, locale);

  // Convert minor → major units
  const amountInMajor = amountInMinor / Math.pow(10, fractionDigits);

  // Use Intl with "compact" notation
  let formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    notation: 'compact', // <-- gives K, M, B, etc.
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amountInMajor);

  if (locale === 'en-IN') {
    formatted = formatted.replace(/(\d+(\.\d+)?)T/, '$1K');
  }
  return formatted;
}

export function convertToMinorCurrency(
  amountInMajor: number,
  currency: SupportedCurrency,
  locale: string
): number {
  const fractionDigits = getCurrencyUnitConvertor(currency, locale);

  // Convert major → minor units
  return Math.round(amountInMajor * Math.pow(10, fractionDigits));
}

export function convertToMajorCurrency(
  amountInMinor: number,
  currency: SupportedCurrency,
  locale: string
): number {
  // Figure out how many fraction digits this currency uses
  const fractionDigits = getCurrencyUnitConvertor(currency, locale);

  // Convert minor → major
  const amountInMajor = amountInMinor / Math.pow(10, fractionDigits);

  return amountInMajor;
}

export function isValidDecimalCurrency(
  value: string,
  currency: SupportedCurrency,
  locale: string
): boolean {
  // Remove commas for safe regex testing
  const cleanValue = value.replace(/,/g, '');

  const fractionDigits = getCurrencyUnitConvertor(currency, locale);

  // Build regex dynamically: up to fractionDigits decimals
  const regex = new RegExp(`^(\\d*)?(\\.\\d{0,${fractionDigits}})?$`);

  return regex.test(cleanValue);
}

export function getMaxDecimalPlaces(currency: SupportedCurrency): number {
  const options = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).resolvedOptions();

  return options.maximumFractionDigits ?? 2;
}
